import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import type { Event } from "@circle-react/types/Event";
import { useToast } from "@circleco/compass/context/CompassContext";
import copyToClipboard from "copy-to-clipboard";
import { useEffect, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";
import { postsPath } from "@/react/helpers/urlHelpers";

interface ShareFormPost {
  minimal_layout: boolean;
  share_post_enabled: boolean;
  share_to_web: boolean;
  show_post_teaser: boolean;
}

export interface ShareFormData {
  post: ShareFormPost;
}

interface SharePostData {
  share_post_link: string;
}

interface UseShareToWebOptionsParams {
  getSharePostLink: (args: { postId: number; body: ShareFormData }) => void;
  post: Event;
  sharePostData: SharePostData | undefined;
}

export const useShareToWebOptions = ({
  post,
  getSharePostLink,
  sharePostData,
}: UseShareToWebOptionsParams) => {
  const toast = useToast();
  const { currentCommunity } = usePunditUserContext();
  const { data: currentSpace } = useCurrentSpaceContext();

  const [formData, setFormData] = useState<ShareFormData>({
    post: {
      show_post_teaser: !!post?.show_post_teaser,
      share_to_web: !!post?.share_post_enabled,
      share_post_enabled: !!post?.share_post_enabled,
      minimal_layout: !!post?.minimal_layout,
    },
  });

  const initialFormDataRef = useRef(formData);

  const postHref = postsPath({ spaceSlug: post?.space_slug, slug: post?.slug });

  const onChange = (sharePostDataToUpdate: Partial<ShareFormPost>) => {
    setFormData(prev => {
      const updatedFormData: ShareFormData = {
        post: {
          ...prev.post,
          ...sharePostDataToUpdate,
        },
      };
      queueMicrotask(() =>
        getSharePostLink({ postId: post?.id, body: updatedFormData }),
      );
      return updatedFormData;
    });
  };

  useEffect(() => {
    if (post.share_post_enabled && !sharePostData) {
      getSharePostLink({ postId: post?.id, body: initialFormDataRef.current });
    }
  }, [post?.share_post_enabled, sharePostData, getSharePostLink, post?.id]);

  const onClickCopyLink = (link: null | string) => {
    copyToClipboard(link || window.location.origin + postHref);
    toast.add({
      title: t("post.share.link_copied"),
      icon: "circle-check-filled",
      iconColor: "success",
      isDismissible: false,
    });
  };

  return {
    onClickCopyLink,
    onChange,
    formData,
    currentCommunity,
    currentSpace,
  };
};
